import { forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button.jsx';
import { IconButton } from './IconButton.jsx';

/**
 * A single toast notification (spec §7.2). `ToastRegion` owns the aria-live
 * plumbing — Toast itself only presents one message, an optional action (the
 * "Einprägen" undo depends on this) and a dismiss control.
 *
 * @param {object} props
 * @param {'success'|'danger'|'signal'} [props.tone] - omit for the neutral default.
 * @param {import('react').ReactNode} [props.title]
 * @param {import('react').ReactNode} props.children - the message body.
 * @param {{label: string, onClick: () => void}} [props.action] - e.g. an "Undo" action.
 * @param {() => void} [props.onDismiss] - omit to render without a dismiss control.
 * @param {string} [props.dismissLabel='Dismiss'] - accessible name for the
 *   dismiss button. ALWAYS pass a translated string (e.g. `t('common.dismiss')`)
 *   in product code — the default is an English fallback only.
 * @param {string} [props.className]
 */
export const Toast = forwardRef(function Toast(
  { tone, title, children, action, onDismiss, dismissLabel = 'Dismiss', className = '', ...rest },
  ref
) {
  return (
    <div ref={ref} className={`etch-toast ${className}`.trim()} data-tone={tone} {...rest}>
      <div className="etch-toast__body">
        {title && <div className="etch-toast__title">{title}</div>}
        {children}
      </div>
      {action && (
        <Button variant="ghost" size="compact" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
      {onDismiss && (
        <IconButton label={dismissLabel} onClick={onDismiss}>
          <X size={16} />
        </IconButton>
      )}
    </div>
  );
});

/**
 * The aria-live region that hosts Toasts (spec §7.2, §12), portaled into
 * `document.body`. Toasts are split into a polite group and an assertive
 * group by tone — `danger` toasts get their own `aria-live="assertive"`
 * region so they interrupt immediately, since a single region can't change
 * its own politeness for toasts already announced to it.
 *
 * @param {object} props
 * @param {{id: string|number, tone?: 'success'|'danger'|'signal', title?: import('react').ReactNode, message: import('react').ReactNode, action?: {label: string, onClick: () => void}, dismissLabel?: string}[]} props.toasts
 * @param {(id: string|number) => void} [props.onDismiss]
 * @param {string} [props.className] - applied to the outer `.etch-toast-region`.
 */
export function ToastRegion({ toasts = [], onDismiss, className = '', ...rest }) {
  if (toasts.length === 0) return null;

  const politeToasts = toasts.filter((toastItem) => toastItem.tone !== 'danger');
  const assertiveToasts = toasts.filter((toastItem) => toastItem.tone === 'danger');

  const renderGroup = (group) =>
    group.map((toastItem) => (
      <Toast
        key={toastItem.id}
        tone={toastItem.tone}
        title={toastItem.title}
        action={toastItem.action}
        dismissLabel={toastItem.dismissLabel}
        onDismiss={onDismiss && (() => onDismiss(toastItem.id))}
      >
        {toastItem.message}
      </Toast>
    ));

  return createPortal(
    <div className={`etch-toast-region ${className}`.trim()} {...rest}>
      {politeToasts.length > 0 && (
        <div className="etch-toast-region__group" role="status" aria-live="polite">
          {renderGroup(politeToasts)}
        </div>
      )}
      {assertiveToasts.length > 0 && (
        <div className="etch-toast-region__group" role="alert" aria-live="assertive">
          {renderGroup(assertiveToasts)}
        </div>
      )}
    </div>,
    document.body
  );
}

export default Toast;
